import React, { useEffect, useState } from 'react'

import { Box, Stack, Typography } from '@mui/material'

import { Skeleton } from '@repo/ui'

import { api } from 'services'
import { useColorMode } from 'hooks'

interface EmailPreviewProps {
  /**
   * A `packages/emails` registry id — `'signup-invitation'`, `'pricing-access-link'`.
   *
   * The SAME id the sending use case passes to `renderEmail`, which is the whole
   * point: the preview and the send resolve one React component out of one
   * registry, so a template edit lands on both or on neither. An id with no
   * registry entry 404s and this renders its error state rather than a blank box.
   */
  templateId: string
  /**
   * Props merged over the template's `sampleProps`.
   *
   * Pass the values the send will genuinely use (a recipient's name, a company);
   * omit anything the CLIENT cannot know. Two things a caller must not invent
   * here are credentials and hosts: a magic-link URL is minted server-side at
   * send time and `pricing-access-link`'s registry entry deliberately samples a
   * DEAD token for exactly this reason. Leaving such a prop out renders the
   * sample, which is a placeholder the reader can recognise as one.
   */
  props?: Record<string, unknown>
  /** Frame height in px. The iframe scrolls internally past it. */
  height?: number
}

/**
 * The real rendered bytes of a transactional email, in an iframe.
 *
 * For a confirmation gate on an action that SENDS something: §43 says the gate
 * must name the consequence, and for "we are about to email this person" the
 * most honest statement of the consequence is the message itself.
 *
 * ## Why it fetches rather than rendering the component here
 *
 * `@repo/emails` is react-email, and a template's final bytes are produced by
 * `renderEmail` on the server — inlined styles, the `prefers-color-scheme`
 * block, brand-token resolution, the `preview` sample-prop merge. Importing the
 * component into the platform bundle and rendering it as ordinary React would
 * produce something that LOOKS like the email and is not it, which is the drift
 * this component exists to make impossible. `GET /internal/labs/emails/:id/preview`
 * is the endpoint the Labs surfaces already use for the same reason (§64).
 *
 * ⚠️ That endpoint is `@RequireAdmin({roles: [PlatformAdmin]})`. Every current
 * caller is already platform-admin-gated; a surface that is not would render the
 * error state, not a leak.
 *
 * ## What the preview is NOT
 *
 * The recipient's own client decides light or dark, so the `mode` here follows
 * the OPERATOR's theme purely so the frame sits naturally in the modal around
 * it. Both renders come from the same component; neither is more real than the
 * other.
 *
 * ## §74, stated precisely rather than optimistically
 *
 * The frame is `width: 100%`, and the email card is `width: 600px; max-width:
 * 100%` (`base-email.tsx`), so it reflows down with the frame. Measured at
 * 1280x800 in a 680px modal: nothing in the dialog overflows, at all.
 *
 * ⚠️ It does NOT reflow indefinitely. React Email lays out in tables, and below
 * roughly the card's own width some rows stop shrinking — at a 600px VIEWPORT
 * the dialog measures 568 and the email gains a horizontal scrollbar OF ITS
 * OWN. That is the §74-sanctioned shape, not a violation of it: wide content
 * that cannot wrap belongs in its own scroll container, and an iframe is one by
 * construction. The MODAL never scrolls sideways; the artifact inside it does,
 * visibly and within its own border.
 *
 * Labs deliberately does the opposite — it pins 600px and scales — because it
 * is reviewing LAYOUT and a reflow would change what is being reviewed. This is
 * answering "what does it say".
 */
export const EmailPreview = ({ templateId, props, height = 360 }: EmailPreviewProps) => {
  const { resolvedMode } = useColorMode()
  const [html, setHtml] = useState<string | null>(null)
  const [error, setError] = useState(false)

  // Serialised so an inline object literal at the call site does not re-fetch on
  // every render of the modal around it.
  const propsKey = props ? JSON.stringify(props) : ''

  useEffect(() => {
    let active = true
    setHtml(null)
    setError(false)
    api
      .get(`/internal/labs/emails/${templateId}/preview`, {
        responseType: 'text',
        params: { mode: resolvedMode === 'dark' ? 'dark' : 'light', ...(propsKey ? { props: propsKey } : {}) },
      })
      .then(({ data }: { data: unknown }) => {
        if (!active) return
        if (typeof data === 'string' && data.length) setHtml(data)
        else setError(true)
      })
      .catch(() => active && setError(true))
    return () => {
      active = false
    }
  }, [templateId, propsKey, resolvedMode])

  return (
    <Box
      sx={{
        height,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* §19 — the skeleton fills the same box the frame will, so nothing shifts
          when the render lands. */}
      {!html && !error && <Skeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: 0 }} />}

      {error && (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', px: 4 }}>
          {/* Named as a preview failure, never as "there is no email". The send
              is a separate call and is unaffected by this one; telling an
              operator the message does not exist would be false. */}
          <Typography variant="body3" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.5 }}>
            Could not render a preview of this email. The message will still be sent.
          </Typography>
        </Stack>
      )}

      {html && (
        <Box
          component="iframe"
          title="Email preview"
          srcDoc={html}
          // The rendered HTML is ours, but it is untrusted-shaped: it carries
          // links and, with brand tokens, company-supplied values. No
          // `allow-scripts`, no `allow-same-origin`, no `allow-popups` — a
          // preview has nothing to run and nowhere to navigate.
          sandbox=""
          sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
        />
      )}
    </Box>
  )
}
